import { DocumentMapper } from "@core/data/mappers/document.mapper";
import { FallbackError } from "@core/domain/errors/fallback.error";
import { IncDocument } from "@core/domain/models/inc-document.model";
import { Nullable } from "@core/domain/types/nullable.type";
import { IVAEnumMapper } from "@entity/data/mappers/cost/iva-enum.mapper";
import { CreateInvestmentExpenseBody } from "@project/data/dto/expenses/create-expense.body";
import { EditInvestmentExpenseBody } from "@project/data/dto/expenses/edit-expense.body";
import { ExpenseSubTypeEnumDto } from "@project/data/dto/expenses/expense-sub-type.dto";
import { ExpenseTypeEnumDto } from "@project/data/dto/expenses/expense-type.dto";
import { ExpenseDto } from "@project/data/dto/expenses/expenses.dto";
import { InvestmentDto } from "@project/data/dto/expenses/investment.dto";
import { ExpenseSubTypeEnumMapper } from "@project/data/mappers/expenses/expense-sub-type-enum.mapper";
import { PaymentMethodTypeEnumMapper } from "@project/data/mappers/expenses/payment-method-type-enum.mapper";
import { CreateManagementExpense } from "@project/domain/models/expenses/create-expense.model";
import { EditManagementExpense } from "@project/domain/models/expenses/edit-expense.model";
import { ExpenseTypeEnum } from "@project/domain/models/expenses/expense-type.model";
import { ManagementExpense } from "@project/domain/models/expenses/expense.model";
import { ExpenseSubTypeEnum } from "@project/domain/models/expenses/sub-type.model";
import { ExpenseManagementFormValuesValidated } from "@project/presentation/components/project-form/expenses/expenses-form/management/expense-management-form.component";
import { validateSync } from "class-validator";
import { inject, injectable } from "inversify";
import { DateTime } from "luxon";

const maximumDecimalsNumber = 2;

@injectable()
export class ManagementExpenseMapper {
    constructor(
        @inject(ExpenseSubTypeEnumMapper)
        private readonly expenseSubTypeMapper: ExpenseSubTypeEnumMapper,
        @inject(PaymentMethodTypeEnumMapper)
        private readonly paymentMethodTypeEnumMapper: PaymentMethodTypeEnumMapper,
        @inject(IVAEnumMapper)
        private readonly ivaEnumMapper: IVAEnumMapper,
        @inject(DocumentMapper)
        private readonly documentMapper: DocumentMapper,
    ) {}
    map(
        expenseDto: ExpenseDto,
        managementData: InvestmentDto,
    ): Nullable<ManagementExpense> {
        const errors = validateSync(managementData);

        if (errors.length > 0) {
            console.error(errors);
            return null;
        }

        const subtype = this.expenseSubTypeMapper.map(
            managementData.subtype_expense,
        );

        let paymentMethod = null;
        if (managementData.payment_method)
            paymentMethod = this.paymentMethodTypeEnumMapper.map(
                managementData.payment_method,
            );

        const iva = this.ivaEnumMapper.map(managementData.iva);
        const documents: IncDocument[] = [];
        if (expenseDto.documents_data) {
            expenseDto.documents_data.forEach((document) => {
                const documentMapped = this.documentMapper.map(document);
                if (documentMapped) {
                    documents.push(documentMapped);
                }
            });
        }

        return new ManagementExpense(
            expenseDto.id,
            "",
            subtype ?? ExpenseSubTypeEnum.NONE,
            "",
            managementData.provider,
            managementData.nif_provider,
            managementData.description,
            managementData.invoice_number,
            managementData.invoice_date
                ? DateTime.fromISO(managementData.invoice_date)
                : null,
            managementData.expense_date
                ? DateTime.fromISO(managementData.expense_date)
                : null,
            paymentMethod,
            managementData.tax_base ? parseFloat(managementData.tax_base) : 0,
            iva,
            managementData.total_tax_with_iva
                ? parseFloat(managementData.total_tax_with_iva)
                : 0,
            managementData.project_allocation_percentage
                ? parseFloat(managementData.project_allocation_percentage)
                : null,
            managementData.amount_allocated_to_project
                ? parseFloat(managementData.amount_allocated_to_project)
                : null,
            managementData.payment_date
                ? DateTime.fromISO(managementData.payment_date)
                : null,
            managementData.concept,
            documents,
            expenseDto.project,
        );
    }

    mapFromFormValues(
        createFormValues: ExpenseManagementFormValuesValidated,
        projectId: number,
    ): CreateManagementExpense {
        return new CreateManagementExpense(
            projectId,
            ExpenseTypeEnum.MANAGEMENT_ADMINISTRATION,
            createFormValues.subTypeId,
            createFormValues.provider,
            createFormValues.nifProvider ? createFormValues.nifProvider : null,
            createFormValues.invoiceNumber,
            createFormValues.invoiceDate,
            createFormValues.invoiceDate,
            createFormValues.paymentMethod,
            createFormValues.base,
            createFormValues.iva,
            createFormValues.baseIva,
            createFormValues.percentageImputation,
            createFormValues.amountImputation,
            createFormValues.paymentDate,
            createFormValues.documents.map((document) => document.id),
            createFormValues.concept,
        );
    }

    mapFromFormEditValues(
        editFormValues: ExpenseManagementFormValuesValidated,
        expenseId: number,
        projectId: number,
    ): EditManagementExpense {
        return new EditManagementExpense(
            expenseId,
            projectId,
            ExpenseTypeEnum.MANAGEMENT_ADMINISTRATION,
            editFormValues.subTypeId,
            editFormValues.provider,
            editFormValues.nifProvider ? editFormValues.nifProvider : null,
            editFormValues.invoiceNumber,
            editFormValues.invoiceDate,
            editFormValues.invoiceDate,
            editFormValues.paymentMethod,
            editFormValues.base,
            editFormValues.iva,
            editFormValues.baseIva,
            editFormValues.percentageImputation,
            editFormValues.amountImputation,
            editFormValues.paymentDate,
            editFormValues.documents.map((document) => document.id),
            editFormValues.concept,
        );
    }

    mapToDto(
        createExpense: CreateManagementExpense,
    ): CreateInvestmentExpenseBody {
        if (!createExpense.paymentMethod) {
            throw new FallbackError();
        }

        const paymentMethod = this.paymentMethodTypeEnumMapper.mapToDto(
            createExpense.paymentMethod,
        );

        let subType = null;
        if (createExpense.subtypeExpense) {
            subType = this.expenseSubTypeMapper.mapToDto(
                createExpense.subtypeExpense,
            );
        }

        const percentageImputation =
            createExpense.percentageImputation?.toFixed(maximumDecimalsNumber);

        const amountAllocatedToProject = createExpense.amountImputation
            ? Number(
                  createExpense.amountImputation.toFixed(maximumDecimalsNumber),
              )
            : 0;

        return {
            project: createExpense.projectId,
            type_expense: ExpenseTypeEnumDto.MANAGEMENT_ADMINISTRATION,
            subtype_expense: subType ?? ExpenseSubTypeEnumDto.NONE,
            amount_allocated_to_project: amountAllocatedToProject,
            project_allocation_percentage: percentageImputation
                ? parseFloat(percentageImputation)
                : 0,
            documents: createExpense.documents,
            concept: createExpense.concept ?? "",
            provider: createExpense.provider,
            nif_provider: createExpense.nifProvider ?? "",
            description: createExpense.concept ?? "", // Dont exist in Management type but we need it...
            invoice_number: createExpense.invoiceNumber ?? "",
            expense_date: createExpense.invoiceDate?.toISODate() ?? "",
            tax_base: createExpense.taxBase?.toString() ?? "0",
            iva: createExpense.iva?.toString() ?? "0",
            total_tax_with_iva:
                createExpense.totalTaxWithIva?.toString() ?? "0",
            payment_method: paymentMethod,
            payment_date: createExpense.paymentDate?.toISODate() ?? "",
        };
    }

    mapToEditDto(
        editExpense: EditManagementExpense,
    ): EditInvestmentExpenseBody {
        if (!editExpense.paymentMethod) {
            throw new FallbackError();
        }

        const paymentMethod = this.paymentMethodTypeEnumMapper.mapToDto(
            editExpense.paymentMethod,
        );
        let subType = null;
        if (editExpense.subtypeExpense) {
            subType = this.expenseSubTypeMapper.mapToDto(
                editExpense.subtypeExpense,
            );
        }

        const percentageImputation = editExpense.percentageImputation?.toFixed(
            maximumDecimalsNumber,
        );

        return {
            id: editExpense.id,
            project: editExpense.projectId,
            type_expense: ExpenseTypeEnumDto.MANAGEMENT_ADMINISTRATION,
            subtype_expense: subType ?? ExpenseSubTypeEnumDto.NONE,
            amount_allocated_to_project: editExpense.amountImputation ?? 0,
            project_allocation_percentage: percentageImputation
                ? parseFloat(percentageImputation)
                : 0,
            documents: editExpense.documents,
            concept: editExpense.concept ?? "",
            provider: editExpense.provider,
            nif_provider: editExpense.nifProvider ?? "",
            description: editExpense.concept ?? "", // Dont exist in Management type but we need it...
            invoice_number: editExpense.invoiceNumber ?? "",
            expense_date: editExpense.invoiceDate?.toISODate() ?? "",
            tax_base: editExpense.taxBase?.toString() ?? "0",
            iva: editExpense.iva?.toString() ?? "0",
            total_tax_with_iva: editExpense.totalTaxWithIva?.toString() ?? "0",
            payment_method: paymentMethod,
            payment_date: editExpense.paymentDate?.toISODate() ?? "",
        };
    }
}
